import style from "./sync-monday-board-modal.module.css";
import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { documentIdAtom } from "state-atoms";
import { MondayIntegrationType } from "shared/integrations/integration";
import consts from "shared/consts";
import { DynamicModal, ModalComponent, ModalProps } from "frontend/modal/dynamic-modal";
import { SearchField } from "frontend/ui-components/search-field/search-field";
import tracking from "frontend/tracking";
import { CloseIcon } from "frontend/icons/close";
import useMondayBoardsPicker from "frontend/hooks/use-monday-boards-picker";
import { MondayBoard } from "shared/integrations/mapper";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import { MondaySharedBoardIcon } from "frontend/canvas-designer-new/integrations/icons/monday-shared-board";
import { MondayPrivateBoardIcon } from "frontend/canvas-designer-new/integrations/icons/monday-private-board";
import { MondayBoardIcon } from "frontend/canvas-designer-new/integrations/icons/monday-board";
import { MondayLogo } from "frontend/icons/monday-logo";
import classNames from "classnames";
import { getPathPrefix } from "utils/getPathPrefix";

type Result = {
  integrationId: string;
};

type SyncMondayBoardModalProps = ModalProps<Result>;

type SyncModalProps = {
  integrationId: string;
};

const SyncMondayBoardModal: ModalComponent<Result, SyncMondayBoardModalProps, SyncModalProps> = ({
  integrationId,
  onComplete,
  onClose,
}: SyncMondayBoardModalProps & SyncModalProps) => {
  const { documentId } = useAtomValue(documentIdAtom);

  const [selectedBoard, setSelectedBoard] = useState<MondayBoard | null>(null);
  const [boardSearchValue, setBoardSearchValue] = useState("");
  const { boards, isLoading, failedToLoad } = useMondayBoardsPicker(integrationId, boardSearchValue);

  const { addMondayIntegration } = useBoardIntegrations(documentId);
  const [isCreatingIntegration, setIsCreatingIntegration] = useState(false);

  useEffect(() => {
    setSelectedBoard(null);
  }, [boardSearchValue]);

  async function onImportClicked() {
    if (!selectedBoard) return;

    tracking.trackEvent(consts.TRACKING_CATEGORY.INTEGRATIONS, "selected_board_to_import", selectedBoard.id);
    try {
      setIsCreatingIntegration(true);
      const newIntegrationId = await addMondayIntegration(
        selectedBoard.id,
        integrationId,
        selectedBoard.columns,
        MondayIntegrationType.tasks
      );
      onComplete({
        integrationId: newIntegrationId,
      });
    } catch (error) {
      console.error("Failed to import board:", error);
    } finally {
      setIsCreatingIntegration(false);
    }
  }

  function renderBoardPickerSkeleton() {
    const rows = Array.from({ length: 6 }, (_, index) => index);
    const rowClasses = rows.map((row) => style[`skeletonRow${(row % 4) + 1}`]);

    return (
      <div className={style.boardsContainer}>
        {rows.map((row) => (
          <div className={rowClasses[row]} key={`skeleton-${row}`} />
        ))}
      </div>
    );
  }

  return (
    <div className={style.container}>
      <CloseIcon onClick={onClose} className={style.closeIcon} />
      <div className={style.header}>
        <div className={style.title}>
          <MondayLogo size={32} />
          Import items from monday
        </div>
        <div className={style.description}>
          Select a board from your monday.com account to import the board&apos;s items to WorkCanvas.
        </div>
        <div className={style.searchContainer}>
          <SearchField
            type="search"
            searchValue={boardSearchValue}
            setSearchValue={setBoardSearchValue}
            placeholder="Search boards"
            className={style.searchField}
            enabled={!isCreatingIntegration}
          />
        </div>
      </div>
      <div className={style.separator} />
      <div className={style.content}>
        {isLoading && <>{renderBoardPickerSkeleton()}</>}

        {failedToLoad && (
          <div className={style.noResultsContainer}>
            <div className={style.noResults}>
              <img src={getPathPrefix("/images/no-results.svg")} alt="" />
              <div className={style.noResultsText}>Failed to load boards</div>
            </div>
          </div>
        )}

        {!isLoading && !selectedBoard && boardSearchValue && boards.length === 0 && (
          <div className={style.noResultsContainer}>
            <div className={style.noResults}>
              <img src={getPathPrefix("/images/no-results.svg")} alt="" />
              <div className={style.noResultsText}>No results</div>
            </div>
          </div>
        )}

        <div className={style.boardsContainer}>
          {!isLoading &&
            boards.map((board) => {
              return (
                <div
                  key={board.id}
                  className={classNames(style.board, {
                    [style.selected]: selectedBoard?.id === board.id,
                  })}
                  onClick={() => setSelectedBoard(board)}
                >
                  {board.board_kind === "public" && <MondayBoardIcon size={26} color="#7F869B" />}
                  {board.board_kind === "private" && <MondayPrivateBoardIcon size={26} color="#7F869B" />}
                  {board.board_kind === "shared" && <MondaySharedBoardIcon size={26} color="#7F869B" />}
                  {/* Fallback in case of unsupported board type */}
                  {!["public", "private", "shared"].includes(board.board_kind) && (
                    <MondayBoardIcon size={25} color="#7F869B" />
                  )}
                  <div className={style.boardName} data-testid={`monday-board-name-${board.name}`}>
                    {board.name}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      <div className={style.separator} />
      <div className={style.footer}>
        <button
          className={style.confirmButton}
          disabled={!selectedBoard || isCreatingIntegration}
          onClick={() => onImportClicked()}
          data-testid="import-board-button"
        >
          {isCreatingIntegration ? "Syncing..." : "Import board"}
        </button>
      </div>
    </div>
  );
};

SyncMondayBoardModal.componentName = "SyncMondayBoardModal";
DynamicModal.register(SyncMondayBoardModal, {
  dimBackground: true,
  dismissOnEscape: true,
  dismissOnBackgroundClick: true,
});

export default SyncMondayBoardModal;
